import { EyeOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Container } from '../../components/Bloco/styles';
import CaixaDeFiltros from '../../components/CaixaDeFiltros';
import {
  consultarHistoricosCadastro,
  obterDetalheHistoricoCadastro,
} from '../../services/historicoCadastro';
import api from '../../services/api';
import * as S from './styles';

const PAGINACAO_INICIAL = { current: 1, pageSize: 10 };
const MINIMO_CARACTERES_USUARIO = 3;
const ESPERA_BUSCA_USUARIO_MS = 300;
const { RangePicker } = DatePicker;

const entidades = [
  { value: 'Cliente', label: 'Cliente' },
  { value: 'Usuario', label: 'Usuário' },
  { value: 'Consulta', label: 'Consulta' },
  { value: 'Matriz', label: 'Matriz' },
];

const rotulosEntidade = entidades.reduce(
  (rotulos, entidade) => ({ ...rotulos, [entidade.value]: entidade.label }),
  {}
);

const rotulosCampos = {
  Id: 'ID',
  Cnpj: 'CNPJ',
  Cpf: 'CPF',
  Email: 'E-mail',
  DataCadastro: 'Data de cadastro',
  DataUltimoLogin: 'Data do último login',
  NomeFantasia: 'Nome fantasia',
  RazaoSocial: 'Razão social',
  IdCliente: 'ID do cliente',
  IdConsulta: 'ID da consulta',
  IdConsultaContigencia: 'ID da consulta de contingência',
  IdConsultaAdicional: 'ID da consulta adicional',
  IdMatriz: 'ID da matriz',
  IdTipoCadastro: 'ID do tipo de cadastro',
  DiasValidadeConsulta: 'Dias de validade da consulta',
  ReprovacaoAutomatica: 'Reprovação automática',
  IdFornecedor: 'ID do fornecedor',
  IdTipoConsulta: 'ID do tipo de consulta',
  IdTipoValidacao: 'ID do tipo de validação',
  EnderecoWebHookFinalizacao: 'Endereço do webhook de finalização',
  EnderecoWebHookStatus: 'Endereço do webhook de status',
  RequestWebhookAuthorization: 'Autorização do webhook',
  UpdatedAt: 'Data de atualização',
};

const formatarCampo = campo =>
  rotulosCampos[campo] || campo.replace(/([a-z])([A-Z])/g, '$1 $2');

const formatarData = data =>
  data ? moment(data).format('DD/MM/YYYY [às] HH:mm:ss') : '-';

const formatarValor = valor =>
  valor === null || valor === undefined || valor === '' ? '-' : valor;

const coresOperacao = {
  Inserida: 'green',
  Atualizada: 'blue',
  Excluída: 'red',
};

export function LogsCadastros() {
  const [form] = Form.useForm();
  const [historicos, setHistoricos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [buscandoUsuarios, setBuscandoUsuarios] = useState(false);
  const temporizadorBuscaUsuarios = useRef(null);
  const sequenciaBuscaUsuarios = useRef(0);
  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [paginacao, setPaginacao] = useState(PAGINACAO_INICIAL);

  const consultar = async (novaPaginacao = PAGINACAO_INICIAL) => {
    setLoading(true);

    try {
      const filtros = form.getFieldsValue();
      const { data } = await consultarHistoricosCadastro({
        Entidade: filtros.entidade || undefined,
        IdUsuario: filtros.idUsuario?.value ?? undefined,
        DataInicio: filtros.periodo?.[0]
          ?.clone()
          .startOf('day')
          .toISOString(),
        DataFim: filtros.periodo?.[1]
          ?.clone()
          .endOf('day')
          .toISOString(),
        Page: novaPaginacao.current,
        NumeroRegistros: novaPaginacao.pageSize,
      });

      setHistoricos(
        (data?.items || []).map(item => ({
          ...item,
          key: `${item.entidade}-${item.id}`,
        }))
      );
      setPaginacao({
        current: data?.pageIndex || novaPaginacao.current,
        pageSize: data?.pageSize || novaPaginacao.pageSize,
        total: data?.count || 0,
      });
    } catch (error) {
      notification.error({
        description: 'Erro ao consultar os logs de cadastros.',
      });
    } finally {
      setLoading(false);
    }
  };

  const buscarUsuarios = valor => {
    const termo = valor.trim();
    sequenciaBuscaUsuarios.current += 1;
    const buscaAtual = sequenciaBuscaUsuarios.current;
    clearTimeout(temporizadorBuscaUsuarios.current);
    setUsuarios([]);

    if (termo.length < MINIMO_CARACTERES_USUARIO) {
      setBuscandoUsuarios(false);
      return;
    }

    setBuscandoUsuarios(true);
    temporizadorBuscaUsuarios.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/Usuario/list', {
          params: { Nome: termo, Page: 1, NumeroRegistros: 20 },
        });
        if (buscaAtual === sequenciaBuscaUsuarios.current) {
          setUsuarios(data?.items || []);
        }
      } catch (error) {
        if (buscaAtual === sequenciaBuscaUsuarios.current) {
          notification.error({
            description: 'Erro ao buscar usuários para o filtro.',
          });
        }
      } finally {
        if (buscaAtual === sequenciaBuscaUsuarios.current) {
          setBuscandoUsuarios(false);
        }
      }
    }, ESPERA_BUSCA_USUARIO_MS);
  };

  useEffect(() => {
    consultar();
    return () => {
      clearTimeout(temporizadorBuscaUsuarios.current);
      sequenciaBuscaUsuarios.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visualizar = async registro => {
    setLoadingDetalhe(true);
    setDetalhe({ ...registro, campos: [] });

    try {
      const { data } = await obterDetalheHistoricoCadastro(
        registro.entidade,
        registro.id
      );
      setDetalhe(data);
    } catch (error) {
      setDetalhe(null);
      notification.error({
        description: 'Erro ao consultar os detalhes da alteração.',
      });
    } finally {
      setLoadingDetalhe(false);
    }
  };

  const limparFiltros = () => {
    form.resetFields();
    buscarUsuarios('');
    consultar(PAGINACAO_INICIAL);
  };

  const colunas = [
    {
      title: 'Entidade',
      dataIndex: 'entidade',
      key: 'entidade',
      render: valor => rotulosEntidade[valor] || valor,
    },
    { title: 'ID', dataIndex: 'idEntidade', key: 'idEntidade', width: 90 },
    {
      title: 'Nome/Descrição',
      dataIndex: 'nomeDescricao',
      key: 'nomeDescricao',
      render: formatarValor,
    },
    {
      title: 'Alterado por',
      dataIndex: 'usuarioAlteracao',
      key: 'usuarioAlteracao',
      render: formatarValor,
    },
    {
      title: 'Data da alteração',
      dataIndex: 'dataAlteracao',
      key: 'dataAlteracao',
      width: 190,
      render: formatarData,
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 90,
      align: 'center',
      render: (_, registro) => (
        <Button
          aria-label="Visualizar alteração"
          icon={<EyeOutlined />}
          onClick={() => visualizar(registro)}
        />
      ),
    },
  ];

  const colunasDetalhe = [
    {
      title: 'Campo',
      dataIndex: 'campo',
      key: 'campo',
      width: '30%',
      render: formatarCampo,
    },
    {
      title: 'Antes',
      dataIndex: 'valorAnterior',
      key: 'valorAnterior',
      render: formatarValor,
    },
    {
      title: 'Depois',
      dataIndex: 'valorAtual',
      key: 'valorAtual',
      render: formatarValor,
    },
  ];

  const filhos = detalhe?.filhos || [];
  const camposCadastro = filhos.length
    ? (detalhe?.campos || []).filter(
        campo => !campo.campo.startsWith('Consulta da matriz')
      )
    : detalhe?.campos || [];

  return (
    <>
      <Container>
        <CaixaDeFiltros>
          <Form layout="vertical" form={form} size="large">
            <Row gutter={[24, 0]}>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="entidade" label="Entidade">
                  <Select
                    allowClear
                    options={entidades}
                    placeholder="Selecione a entidade"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="idUsuario" label="Usuário">
                  <Select
                    allowClear
                    labelInValue
                    showSearch
                    filterOption={false}
                    onSearch={buscarUsuarios}
                    onClear={() => buscarUsuarios('')}
                    notFoundContent={
                      buscandoUsuarios ? (
                        <Spin size="small" />
                      ) : (
                        'Digite ao menos 3 caracteres'
                      )
                    }
                    options={usuarios.map(usuario => ({
                      value: usuario.id,
                      label: usuario.nome,
                    }))}
                    placeholder="Digite 3 caracteres para buscar"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="periodo" label="Data da alteração">
                  <RangePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row justify="end">
              <Space>
                <Button onClick={limparFiltros}>Limpar</Button>
                <Button
                  type="primary"
                  onClick={() => consultar(PAGINACAO_INICIAL)}
                >
                  Filtrar
                </Button>
              </Space>
            </Row>
          </Form>
        </CaixaDeFiltros>
      </Container>

      <Container>
        <Table
          columns={colunas}
          dataSource={historicos}
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            ...paginacao,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: total =>
              `Total de ${total} ${total === 1 ? 'item' : 'itens'}`,
          }}
          onChange={consultar}
        />
      </Container>

      <Modal
        title="Detalhes da alteração"
        open={!!detalhe}
        width="90%"
        footer={null}
        onCancel={() => setDetalhe(null)}
      >
        <Typography.Paragraph>
          <Typography.Text strong>Registro: </Typography.Text>
          {rotulosEntidade[detalhe?.entidade] || detalhe?.entidade}
          {' #'}
          {detalhe?.idEntidade}
          {' — '}
          {formatarValor(detalhe?.nomeDescricao)}
        </Typography.Paragraph>
        <Typography.Paragraph>
          <Typography.Text strong>Alterado por: </Typography.Text>
          {formatarValor(detalhe?.usuarioAlteracao)}
          {' em '}
          {formatarData(detalhe?.dataAlteracao)}
        </Typography.Paragraph>
        <Typography.Title level={5}>Campos do cadastro</Typography.Title>
        <S.TabelaDetalhes
          rowKey="campo"
          columns={colunasDetalhe}
          dataSource={camposCadastro}
          loading={loadingDetalhe}
          pagination={false}
          scroll={{ x: 750, y: '55vh' }}
          rowClassName={registro => (registro.alterado ? 'linha-alterada' : '')}
        />
        {filhos.length > 0 && (
          <>
            <Typography.Title level={5}>Consultas da matriz</Typography.Title>
            {filhos.map(filho => (
              <S.GrupoFilho key={`${filho.entidade}-${filho.idRegistro}`}>
                <Space wrap>
                  <Typography.Text strong>
                    {`Consulta #${formatarValor(filho.idConsulta)}`}
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    {`Vínculo ${
                      filho.idRegistro.startsWith('novo-')
                        ? 'novo'
                        : `#${filho.idRegistro}`
                    }`}
                  </Typography.Text>
                  <Tag color={coresOperacao[filho.operacao] || 'default'}>
                    {filho.operacao}
                  </Tag>
                </Space>
                <S.TabelaDetalhes
                  rowKey="campo"
                  columns={colunasDetalhe}
                  dataSource={filho.campos || []}
                  pagination={false}
                  scroll={{ x: 750 }}
                  rowClassName={registro =>
                    registro.alterado ? 'linha-alterada' : ''
                  }
                />
              </S.GrupoFilho>
            ))}
          </>
        )}
      </Modal>
    </>
  );
}
